import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import styled from 'styled-components';
import { Modal } from '../components/Modal';
import { Button } from '../components/Button';

type DialogTone = 'default' | 'danger' | 'success';

interface ConfirmOptions {
  title?: string;
  message: ReactNode;
  confirmText?: string;
  cancelText?: string;
  tone?: DialogTone;
}

interface AlertOptions {
  title?: string;
  message: ReactNode;
  confirmText?: string;
  tone?: DialogTone;
}

interface DialogContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  alert: (options: AlertOptions) => Promise<void>;
}

const DialogContext = createContext<DialogContextValue | null>(null);

interface InternalState {
  kind: 'confirm' | 'alert';
  title: string;
  message: ReactNode;
  confirmText: string;
  cancelText: string;
  tone: DialogTone;
}

export function DialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<InternalState | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const close = useCallback((result: boolean) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setState(null);
  }, []);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
        setState({
          kind: 'confirm',
          title: options.title ?? '확인',
          message: options.message,
          confirmText: options.confirmText ?? '확인',
          cancelText: options.cancelText ?? '취소',
          tone: options.tone ?? 'default',
        });
      }),
    []
  );

  const alert = useCallback(
    (options: AlertOptions) =>
      new Promise<void>((resolve) => {
        resolveRef.current = () => resolve();
        setState({
          kind: 'alert',
          title: options.title ?? '알림',
          message: options.message,
          confirmText: options.confirmText ?? '확인',
          cancelText: '',
          tone: options.tone ?? 'default',
        });
      }),
    []
  );

  const value = useMemo(() => ({ confirm, alert }), [confirm, alert]);

  const open = state !== null;
  const confirmVariant =
    state?.tone === 'danger'
      ? 'danger'
      : state?.tone === 'success'
        ? 'primary'
        : 'primary';

  return (
    <DialogContext.Provider value={value}>
      {children}
      <Modal
        open={open}
        onClose={() => close(false)}
        labelledBy="dialog-title"
        describedBy="dialog-message"
        width={420}
      >
        {state && (
          <Body>
            <Title id="dialog-title" $tone={state.tone}>
              {state.title}
            </Title>
            <Message id="dialog-message">{state.message}</Message>
            <Actions>
              {state.kind === 'confirm' && (
                <Button
                  $variant="ghost"
                  $size="sm"
                  onClick={() => close(false)}
                >
                  {state.cancelText}
                </Button>
              )}
              <Button
                $variant={confirmVariant}
                $size="sm"
                onClick={() => close(true)}
              >
                {state.confirmText}
              </Button>
            </Actions>
          </Body>
        )}
      </Modal>
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return ctx;
}

const Body = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h2<{ $tone: DialogTone }>`
  font-size: 17px;
  font-weight: 700;
  color: ${({ $tone, theme }) =>
    $tone === 'danger' ? theme.colors.danger : theme.colors.textPrimary};
`;

const Message = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: pre-line;
  word-break: break-word;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;
